import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import api from '../../services/api';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';
import TextInput from '../../components/ui/TextInput.jsx';
import Button from '../../components/ui/Button';
import InviteLinkDialog from '../visits/InviteLinkDialog';
import PersonCell from '../../components/ui/PersonCell';

const Container = styled.div``;

const RejectOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 42, 42, 0.4);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const RejectDialog = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 28px;
  max-width: 420px;
  width: 90%;
  box-shadow: 0 16px 48px rgba(0, 66, 66, 0.15);
`;

const RejectTitle = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 16px;
`;

const RejectActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
`;

export default function VisitApprovalsPage() {
  const { t, i18n } = useTranslation();

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [approveConfirm, setApproveConfirm] = useState({ isOpen: false, id: null, name: '' });
  const [rejectDialog, setRejectDialog] = useState({ isOpen: false, id: null });
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [inviteDialog, setInviteDialog] = useState({ open: false, link: null, smsSent: false });
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/visits/pending-approvals?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const handleApproveConfirm = useCallback(async () => {
    setProcessing(true);
    try {
      const res = await api.put(`/v1/visits/${approveConfirm.id}/approve`);
      const d = res.data?.data;
      // SMS gönderilemediyse davet linki onaycıya gösterilir (ekran fallback)
      if (d && d.inviteLink) {
        setInviteDialog({ open: true, link: d.inviteLink, smsSent: false });
      } else if (d && d.smsSent) {
        setInviteDialog({ open: true, link: null, smsSent: true });
      }
      setRefreshTrigger((p) => p + 1);
    } catch { /* hata toast'ı interceptor'da */ }
    setApproveConfirm({ isOpen: false, id: null, name: '' });
    setProcessing(false);
  }, [approveConfirm.id]);

  const handleRejectConfirm = useCallback(async () => {
    if (!rejectReason.trim()) return;
    setProcessing(true);
    try {
      await api.put(`/v1/visits/${rejectDialog.id}/reject`, { rejectionReason: rejectReason });
      setRefreshTrigger((p) => p + 1);
    } catch { /* hata toast'ı interceptor'da */ }
    setRejectDialog({ isOpen: false, id: null });
    setProcessing(false);
  }, [rejectDialog.id, rejectReason]);

  const baseColumns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'visitorFullName', label: t('visit.visitorFullName'), options: { filter: false, sort: false } },
    { name: 'createdByName', label: t('visit.createdBy'), options: { filter: false, sort: false, customBodyRender: (v) => v || '-' } },
    { name: 'hostEmployeeName', label: t('visit.hostEmployee'), options: {
      filter: false, sort: false,
      customBodyRender: (v, tableMeta) => (
        v ? <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.hostEmployeeProfilePicture} /> : '-'
      ) } },
    {
      name: 'validFrom',
      label: t('visit.validFrom'),
      options: { filter: false, sort: false, customBodyRender: (v) => (v ? new Date(v).toLocaleString(i18n.language) : '-') },
    },
    {
      name: 'validTo',
      label: t('visit.validTo'),
      options: { filter: false, sort: false, customBodyRender: (v) => (v ? new Date(v).toLocaleString(i18n.language) : '-') },
    },
    {
      name: 'gates',
      label: t('visit.gates'),
      options: { filter: false, sort: false, customBodyRender: (v) => (v || []).map((g) => g.name).join(', ') || '-' },
    },
  ], [t, i18n.language, data]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const actionOptions = useMemo(() => [
    { type: 'submit_for_approval', label: t('visit.approve'), onClick: (row) => setApproveConfirm({ isOpen: true, id: row.id, name: row.visitorFullName }) },
    { type: 'cancel', label: t('visit.reject'), onClick: (row) => { setRejectDialog({ isOpen: true, id: row.id }); setRejectReason(''); } },
  ], [t]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return <OptionsMenu actions={actionOptions} row={row} />;
        },
      },
    },
  ], [baseColumns, actionOptions, data]);

  return (
    <Container>
      <InviteLinkDialog
        open={inviteDialog.open}
        link={inviteDialog.link}
        smsSent={inviteDialog.smsSent}
        onClose={() => setInviteDialog({ open: false, link: null, smsSent: false })}
      />

      <ConfirmDialog
        isOpen={approveConfirm.isOpen}
        title={t('visit.approveTitle')}
        message={t('visit.approveMessage', { name: approveConfirm.name })}
        onConfirm={handleApproveConfirm}
        onCancel={() => setApproveConfirm({ isOpen: false, id: null, name: '' })}
        loading={processing}
      />

      {rejectDialog.isOpen && (
        <RejectOverlay>
          <RejectDialog>
            <RejectTitle>{t('visit.rejectTitle')}</RejectTitle>
            <TextInput
              label={t('visit.rejectReason')}
              type="textarea"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              required
              rows={3}
            />
            <RejectActions>
              <Button onClick={() => setRejectDialog({ isOpen: false, id: null })}>{t('guest.back')}</Button>
              <Button onClick={handleRejectConfirm} disabled={processing || !rejectReason.trim()}>
                {t('visit.reject')}
              </Button>
            </RejectActions>
          </RejectDialog>
        </RejectOverlay>
      )}

      {useMemo(() => (
        <DataTable
          title={t('visit.approvalsTitle')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination,
        filterList, handleFilterConfirm, handleDataStateChange,
      ])}
    </Container>
  );
}
